import { useTranslation } from "react-i18next";
import { useToast } from "context/ToastContext";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import { createPortal } from "react-dom";
import SvgEdit from "components/svg/SvgEdit";
import SvgUser from "components/svg/SvgUser";
import SvgCheckSquare from "components/svg/SvgCheckSquare";
import { TeamSelect } from 'modules/user';
import { useEffect, useRef, useState } from "react";
import { useObject } from "../../context/ObjectProvider";
import * as apiObject from "api/apiObjects";
import { useSettings } from "context/SettingsContext";
import SvgCheck from "components/svg/SvgCheck";
import SvgSun from "components/svg/SvgSun";
import useSettingsParams from "hooks/useSettingsParams";

const MENU_SCROLL_PAD = 16;

const clearBodyScrollPad = () => {
    if (typeof document === 'undefined') return;
    document.body.style.paddingBottom = '';
};

/** Add page scroll room and scroll so the full menu becomes reachable. */
const ensurePageScrollForMenu = (menuEl) => {
    if (!menuEl || typeof window === 'undefined') return;

    const rect = menuEl.getBoundingClientRect();
    const overflowBottom = Math.ceil(rect.bottom - window.innerHeight + MENU_SCROLL_PAD);
    if (overflowBottom <= 0) return;

    const currentPad = parseInt(document.body.style.paddingBottom || '0', 10) || 0;
    document.body.style.paddingBottom = `${currentPad + overflowBottom}px`;
    window.scrollBy({ top: overflowBottom, behavior: 'auto' });
};

const ObjectAction = ({ vobject, fetchObjects, setClientInfoId, setTasksObjectId, setShowFormName, onShowSpecification }) => {
    const { getSettings } = useSettings();
    const { showToast } = useToast();
    const { t } = useTranslation(['Objects', 'Clients', 'common']);
    const { setShowSpinner } = useObject();
    const navigate = useNavigate();
    const [currentTeamId, setCurrentTeamId] = useState(vobject?.teamId);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const params = useSettingsParams();
    const canShowTasks = params.tasksModule && params.tasksForObjects;

    useEffect(() => {
        if (!menuOpen) {
            clearBodyScrollPad();
            return undefined;
        }

        // Wait for Popper to position the menu, then make the page scrollable enough.
        const timer = window.setTimeout(() => {
            ensurePageScrollForMenu(menuRef.current);
        }, 50);

        return () => {
            window.clearTimeout(timer);
            clearBodyScrollPad();
        };
    }, [menuOpen]);

    const handleChangeTeam = async () => {
        setShowSpinner(true);
        const resTeam = await apiObject.setTeam(vobject.ID, currentTeamId);
        await getSettings();
        await fetchObjects(true);
        if (resTeam) showToast(t('messTeamChanged'), 'success');
        else showToast(t('common:messError'), 'danger');
        setShowSpinner(false);
    };

    const handleChangeVat = async () => {
        setShowSpinner(true);
        const resVat = await apiObject.setVat(vobject.ID, vobject.objectVat ? 0 : 1);
        await fetchObjects(true);
        if (resVat) showToast(t('messVatChanged'), 'success');
        else showToast(t('common:messError'), 'danger');
        setShowSpinner(false);
    };

    const renderMenuItem = (icon, title, subtitle, onSelect, options) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            ref={menuRef}
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{
                strategy: 'fixed',
                modifiers: [
                    // Allow menu to extend past the viewport; page scroll reveals the rest.
                    { name: 'preventOverflow', enabled: false },
                    {
                        name: 'flip',
                        options: { fallbackPlacements: ['top-end', 'bottom-end'] },
                    },
                ],
            }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('objectPopup.editName'),
                t('objectPopup.editNameHint'),
                () => setShowFormName(true),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgSun />,
                t('objectPopup.open'),
                t('objectPopup.openHint'),
                () => navigate(`/objects/${vobject.ID}`),
            )}

            {renderMenuItem(
                <i className="bi bi-card-list"></i>,
                t('objectPopup.specification'),
                t('objectPopup.specificationHint'),
                () => onShowSpecification?.(vobject),
            )}

            {canShowTasks && renderMenuItem(
                <SvgCheckSquare width={16} height={16} />,
                t('objectPopup.task'),
                t('objectPopup.taskHint'),
                () => setTasksObjectId?.(vobject.ID),
            )}

            {renderMenuItem(
                <SvgUser />,
                t('objectPopup.client'),
                t('Clients:clientPopup.viewHint'),
                () => setClientInfoId(vobject.clientId),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgCheck style={{ opacity: vobject.objectVat ? 1 : 0 }} />,
                t('objectPopup.vat'),
                t('objectPopup.vatHint'),
                handleChangeVat,
            )}

            <Dropdown.Divider className="my-2" />

            <Dropdown.Item as="div" className="object-action-team-item">
                <div className="d-flex align-items-start w-100 text-start">
                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                        <i className="bi bi-people"></i>
                    </span>
                    <div className="flex-grow-1 min-w-0">
                        <div className="fw-medium">{t('objectPopup.team')}</div>
                        <small className="text-muted warehouse-team-menu-hint d-block mb-2">
                            {t('objectPopup.teamHint')}
                        </small>
                        <div className="show-dropdown-svg" onClick={(e) => e.stopPropagation()}>
                            <TeamSelect
                                hideLabel
                                showEmpty={true}
                                value={currentTeamId || null}
                                onChange={(opt) => {
                                    setCurrentTeamId(opt.value);
                                }}
                            />
                        </div>
                        <Button
                            className="w-100 mt-2"
                            disabled={currentTeamId === vobject?.teamId}
                            size="sm"
                            variant="info"
                            onClick={(e) => {
                                e.currentTarget.blur();
                                void handleChangeTeam();
                            }}
                        >
                            {t('common:buttons.change')}
                        </Button>
                    </div>
                </div>
            </Dropdown.Item>
        </Dropdown.Menu>
    );

    return (
        <Dropdown
            align="end"
            onToggle={(isOpen) => {
                setMenuOpen(isOpen);
                if (isOpen) setCurrentTeamId(vobject?.teamId);
            }}
        >
            <Dropdown.Toggle className="p-1 py-0" size="sm" variant="light-secondary" id={`dropdown-object-${vobject.ID}`}>
                <i className="bi bi-three-dots"></i>
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ObjectAction;
